import type { ReactElement } from "react";

import type { ApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { loadFailureText, type LoadFailure } from "../../components/load-failure.js";
import { NotFoundPage } from "../account/message-pages.js";

import { tenantUrls } from "./urls.js";

/**
 * 読み込み中と、読めなかったとき（契約の画面で共通）。
 *
 * <b>404 は「見つからない」画面にする</b>（ほかの画面と同じ。<c>ApiState</c>）。
 * 所属していない契約も、無い契約も、解約済みの契約も 404 で返る——<b>理由は出し分けない</b>
 * （存在の有無を出さない）。「読み込めませんでした」はやり直せば開けそうに読めるので、通信できなかったときだけ。
 */
export function LoadingPage({ title, data, width = "max-w-3xl" }: {
    readonly title: string;
    readonly data: ApiData<unknown>;
    readonly width?: string;
}): ReactElement {
    if (data.state === "failed" && data.status === 404) {
        return <NotFoundPage />;
    }
    return <Pending title={title} failure={data.state === "failed" ? data : undefined} width={width} />;
}

function Pending({ title, failure, width }: {
    readonly title: string;
    readonly failure: LoadFailure | undefined;
    readonly width: string;
}): ReactElement {
    usePageMeta(title);

    return (
        <div className={`page ${width}`}>
            <h1 className="page-title">{title}</h1>
            {failure === undefined
                ? <p className="text-slate-500">読み込んでいます…</p>
                : (
                    <div className="alert-danger" role="alert">
                        <p>読み込めませんでした。{loadFailureText(failure).reason}契約一覧からもう一度お試しください。</p>
                        {/* 操作の入口は文中のリンクにせず、文の下にボタンで置く（docs/01 §6） */}
                        <p className="mt-3"><a href={tenantUrls.list} className="btn-secondary btn-sm">契約一覧</a></p>
                    </div>
                )}
        </div>
    );
}
